import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControlLabel,
    IconButton,
    MenuItem,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import integrationStatusServices from "app/services/integration-status-services";
import { timeAgo } from "app/pages/pulse/pulseFormat";

const LOGOS = {
    "mercado-libre": "/images/icons/mercado-libre-color.svg",
    "relbase-sale-notes": "/images/icons/relbase-sale-notes-color.svg",
    "relbase-invoices": "/images/icons/relbase-invoices-color.svg",
    bsale: "/images/icons/bsale.svg",
    shopify: "/images/icons/shopify.svg",
    "bluexpress-tracking": "/images/icons/blueexpress.svg",
    "starken-tracking": "/images/icons/starken2.svg",
};

const LEVELS = {
    error: { label: "Falla", color: "error", rank: 4 },
    warning: { label: "Atención", color: "warning", rank: 3 },
    info: { label: "Sin actividad aún", color: "info", rank: 2 },
    ok: { label: "OK", color: "success", rank: 1 },
    off: { label: "Apagada", color: "default", rank: 0 },
};

// El estado de la fila junta lo que Haulers ya sabe con lo que contesto la prueba en vivo:
// manda el peor de los dos. Una integracion apagada queda apagada aunque la prueba diga algo.
const combinedLevel = (row, live) => {
    if (row.level === "off") return "off";
    const levels = [row.level, live?.level].filter((l) => LEVELS[l]);
    return levels.reduce((worst, l) => (LEVELS[l].rank > LEVELS[worst].rank ? l : worst), "ok");
};

const until = (value) => (value ? moment(value).locale("es").fromNow(true) : "-");

const LevelChip = ({ level, pending }) => {
    if (pending) return <Chip size="small" variant="outlined" label="Probando…" icon={<CircularProgress size={12} />} />;
    const meta = LEVELS[level] || LEVELS.ok;
    return <Chip size="small" color={meta.color} variant={level === "ok" ? "outlined" : "filled"} label={meta.label} />;
};

// La cookie de Relbase es lo que mas se cae (login con reCAPTCHA v3, se re-siembra a mano), por
// eso va con chip propio: vigente / vencida / sin verificar, y donde vive.
const RelbaseCookie = ({ summary, live }) => {
    const cookie = live?.cookie;
    let label = "Cookie sin verificar";
    let color = "default";
    if (cookie?.valid === true) { label = "Cookie vigente"; color = "success"; }
    else if (cookie?.valid === false) { label = cookie.message?.startsWith("No hay") ? "Sin cookie" : "Cookie vencida"; color = "error"; }
    else if (summary?.present === false && summary?.mode === "db") { label = "Sin cookie"; color = "error"; }

    const WHERE = {
        db: "guardada en la base, por empresa",
        cookie_jobs: "la renueva el PC de oficina (cookie_jobs)",
        file: "archivo compartido del server (legacy)",
    };
    const renewal = summary?.renewal;
    const open = renewal?.open;
    return (
        <Stack spacing={0.25}>
            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Tooltip title={cookie?.message || "Todavía no se prueba contra app.relbase.cl"}>
                    <Chip size="small" color={color} variant={color === "default" ? "outlined" : "filled"} label={label} />
                </Tooltip>
                <Typography variant="caption" color="text.secondary">{WHERE[summary?.mode] || WHERE.file}</Typography>
                {summary?.written_at && <Typography variant="caption" color="text.secondary">· escrita {timeAgo(summary.written_at)}</Typography>}
                {summary?.renewed_at && <Typography variant="caption" color="text.secondary">· renovada {timeAgo(summary.renewed_at)}</Typography>}
            </Stack>
            {open && (
                <Typography variant="caption" color={open.last_error ? "error.main" : "warning.dark"}>
                    Renovación {open.status === "en_proceso" ? "en curso" : "pendiente"}: intento {open.attempts} de {renewal.max_attempts}
                    {open.created_at ? `, pedida ${timeAgo(open.created_at)}` : ""}
                </Typography>
            )}
            {renewal && (
                <Typography variant="caption" color="text.secondary">
                    En 24 h: {renewal.renewals_24h} {renewal.renewals_24h === 1 ? "renovación" : "renovaciones"}
                    {renewal.failures_24h > 0 ? ` · ${renewal.failures_24h} ${renewal.failures_24h === 1 ? "fallida" : "fallidas"}` : ""}
                </Typography>
            )}
        </Stack>
    );
};

const Details = ({ row, live }) => {
    const d = row.details || {};
    const slug = row.plugin.slug;
    const lines = [];

    if (slug === "mercado-libre") {
        (d.accounts || []).forEach((acc) => {
            const probe = (live?.accounts || []).find((a) => a.name === acc.nickname);
            lines.push(
                <Typography key={acc.uid} variant="caption" display="block" color={acc.expired || probe?.ok === false ? "error.main" : "text.secondary"}>
                    {acc.nickname || acc.uid} · token {acc.expired ? "vencido" : `vence en ${until(acc.expires_at)}`} · renovado {timeAgo(acc.refreshed_at)}
                </Typography>
            );
        });
    }
    if (slug === "relbase-sale-notes" || slug === "relbase-invoices") {
        lines.push(<RelbaseCookie key="cookie" summary={d.cookie} live={live} />);
        if (live?.api) {
            lines.push(<Typography key="api" variant="caption" display="block" color={live.api.ok ? "text.secondary" : "error.main"}>API: {live.api.message}</Typography>);
        }
    }
    if (slug === "shopify" && d.shop_domain) {
        lines.push(
            <Typography key="shop" variant="caption" display="block" color="text.secondary">
                {d.shop_domain} · {d.ingests_orders ? "trae pedidos" : "solo devuelve el despacho"}{d.push_trigger_state ? ` (al quedar ${d.push_trigger_state})` : ""}
            </Typography>
        );
    }
    if (slug === "bsale" && d.connected_at) {
        lines.push(<Typography key="bsale" variant="caption" display="block" color="text.secondary">Conectada {timeAgo(d.connected_at)}</Typography>);
    }
    if (slug === "starken-tracking") {
        lines.push(<Typography key="starken" variant="caption" display="block" color="text.secondary">Último envío actualizado {timeAgo(d.last_shipment_update_at)}</Typography>);
    }
    if (row.job) {
        lines.push(
            <Typography key="job" variant="caption" display="block" color={row.job.last_status === "error" ? "error.main" : "text.secondary"}>
                Job: corrió {timeAgo(row.job.last_started_at)}{row.job.errors_24h > 0 ? ` · ${row.job.errors_24h} fallas en 24 h` : ""}
            </Typography>
        );
    }
    return lines.length ? <Stack spacing={0.25}>{lines}</Stack> : <Typography variant="caption" color="text.secondary">-</Typography>;
};

const Messages = ({ row, live }) => {
    const items = [...row.issues];
    if (live && live.message && row.level !== "off" && (live.ok === false || live.level === "warning")) {
        items.unshift({ level: live.level || "error", message: `En vivo: ${live.message}` });
    }
    if (items.length === 0) {
        return <Typography variant="body2" color="text.secondary">{live?.message || "Sin problemas"}</Typography>;
    }
    return (
        <Stack spacing={0.25}>
            {items.map((i, idx) => (
                <Typography key={idx} variant="body2" color={i.level === "error" ? "error.main" : i.level === "warning" ? "warning.dark" : "text.secondary"}>
                    {i.message}
                </Typography>
            ))}
        </Stack>
    );
};

const CompanyCard = ({ company, rows, checks, pending, onRetry }) => {
    const worst = rows.reduce((w, r) => {
        const l = combinedLevel(r, checks[r.id]);
        return LEVELS[l].rank > LEVELS[w].rank ? l : w;
    }, "off");

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h5" sx={{ mb: 0 }}>{company.name}</Typography>
                    <LevelChip level={worst} />
                    <Typography variant="caption" color="text.secondary">#{company.id}</Typography>
                </Stack>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Integración</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell sx={{ minWidth: 240 }}>Qué pasa</TableCell>
                                <TableCell>Último pedido</TableCell>
                                <TableCell sx={{ minWidth: 220 }}>Detalle</TableCell>
                                <TableCell>Probado</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => {
                                const live = checks[row.id];
                                const isPending = row.live_check && row.enabled && pending.has(row.id);
                                return (
                                    <TableRow key={row.id} hover>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                {LOGOS[row.plugin.slug]
                                                    ? <Box component="img" src={LOGOS[row.plugin.slug]} alt="" sx={{ width: 22, height: 22, objectFit: "contain" }} />
                                                    : <Box sx={{ width: 22 }} />}
                                                <Typography variant="body2"><b>{row.plugin.name}</b></Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell><LevelChip level={combinedLevel(row, live)} pending={isPending && !live} /></TableCell>
                                        <TableCell><Messages row={row} live={live} /></TableCell>
                                        <TableCell>
                                            {row.orders ? (
                                                <>
                                                    <Typography variant="body2">{timeAgo(row.orders.last_at)}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{row.orders.last_24h} en 24 h · {row.orders.last_7d} en 7 días</Typography>
                                                </>
                                            ) : <Typography variant="caption" color="text.secondary">No trae pedidos</Typography>}
                                        </TableCell>
                                        <TableCell><Details row={row} live={live} /></TableCell>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                <Typography variant="caption" color="text.secondary">{live?.checked_at ? timeAgo(live.checked_at) : "-"}</Typography>
                                                {row.live_check && row.enabled && (
                                                    <Tooltip title="Probar de nuevo">
                                                        <span>
                                                            <IconButton size="small" disabled={isPending} onClick={() => onRetry([row.id])}>
                                                                {isPending ? <CircularProgress size={16} /> : <RefreshOutlinedIcon fontSize="small" />}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
};

// Estado de las integraciones de cada cliente. Solo el equipo de Kargotools (superadmin con
// correo @kargotools.com, el mismo criterio que el backend y que el Pulso): cruza todas las empresas.
const IntegrationStatusIndex = () => {
    const navigate = useNavigate();
    const { authUser } = useJumboAuth();
    const [overview, setOverview] = useState(null);
    const [checks, setChecks] = useState({});
    const [pending, setPending] = useState(new Set());
    const [companyId, setCompanyId] = useState("");
    const [onlyProblems, setOnlyProblems] = useState(false);
    const [error, setError] = useState(null);

    const allowed = useMemo(() => {
        const superadmin = (authUser?.profiles || []).some((p) => p.name === "superadmin");
        return superadmin && /@kargotools\.com$/i.test(authUser?.email || "");
    }, [authUser]);

    useEffect(() => {
        if (authUser && !allowed) navigate("/trips");
    }, [authUser, allowed, navigate]);

    const runChecks = useCallback(async (ids, force) => {
        setPending((prev) => new Set([...prev, ...ids]));
        try {
            const data = await integrationStatusServices.checks({ ids: force ? ids : undefined, force });
            setChecks((prev) => ({ ...prev, ...data }));
        } catch (e) {
            const failed = {};
            ids.forEach((id) => { failed[id] = { ok: false, level: "warning", message: "No se pudo correr la prueba", checked_at: new Date().toISOString() }; });
            setChecks((prev) => ({ ...prev, ...failed }));
        } finally {
            setPending((prev) => {
                const next = new Set(prev);
                ids.forEach((id) => next.delete(id));
                return next;
            });
        }
    }, []);

    const load = useCallback(async (force = false) => {
        setError(null);
        try {
            const data = await integrationStatusServices.overview();
            setOverview(data);
            const ids = data.data.filter((r) => r.live_check && r.enabled).map((r) => r.id);
            if (ids.length) runChecks(ids, force);
        } catch (e) {
            setError("No se pudo cargar el estado de las integraciones.");
            setOverview({ data: [] });
        }
    }, [runChecks]);

    useEffect(() => {
        if (allowed) load(false);
    }, [allowed, load]);

    const rows = useMemo(() => overview?.data || [], [overview]);

    const companies = useMemo(() => {
        const map = new Map();
        rows.forEach((r) => { if (!map.has(r.company.id)) map.set(r.company.id, r.company); });
        return [...map.values()].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }, [rows]);

    const counts = useMemo(() => {
        const c = { error: 0, warning: 0, info: 0, ok: 0, off: 0 };
        rows.forEach((r) => { c[combinedLevel(r, checks[r.id])] += 1; });
        return c;
    }, [rows, checks]);

    const groups = useMemo(() => {
        const visible = rows.filter((r) => {
            if (companyId && `${r.company.id}` !== `${companyId}`) return false;
            if (onlyProblems) {
                const l = combinedLevel(r, checks[r.id]);
                return l === "error" || l === "warning";
            }
            return true;
        });
        const byCompany = new Map();
        visible.forEach((r) => {
            if (!byCompany.has(r.company.id)) byCompany.set(r.company.id, { company: r.company, rows: [] });
            byCompany.get(r.company.id).rows.push(r);
        });
        return [...byCompany.values()].sort((a, b) => (a.company.name || "").localeCompare(b.company.name || ""));
    }, [rows, checks, companyId, onlyProblems]);

    if (!allowed) return null;

    return (
        <Stack spacing={2}>
            <div>
                <Typography variant="h2" sx={{ mb: 0.5 }}>Estado de integraciones</Typography>
                <Typography variant="body2" color="text.secondary">
                    Cómo está cada integración de cada cliente: si tiene sus credenciales, si le siguen entrando pedidos y si el proveedor contesta ahora. En Relbase, además, si la cookie de sesión sigue vigente.
                </Typography>
            </div>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
                <TextField select size="small" label="Cliente" value={companyId} onChange={(e) => setCompanyId(e.target.value)} sx={{ minWidth: 240 }}>
                    <MenuItem value="">Todos los clientes</MenuItem>
                    {companies.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </TextField>
                <FormControlLabel control={<Switch checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} />} label="Solo con problemas" />
                <Box sx={{ flexGrow: 1 }} />
                <Button variant="outlined" startIcon={<RefreshOutlinedIcon />} disabled={!overview || pending.size > 0}
                        onClick={() => load(true)}>
                    Probar todo de nuevo
                </Button>
            </Stack>

            {overview && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip color="error" label={`${counts.error} con falla`} variant={counts.error ? "filled" : "outlined"} />
                    <Chip color="warning" label={`${counts.warning} con atención`} variant={counts.warning ? "filled" : "outlined"} />
                    <Chip color="success" variant="outlined" label={`${counts.ok} OK`} />
                    {counts.info > 0 && <Chip color="info" variant="outlined" label={`${counts.info} sin actividad aún`} />}
                    {counts.off > 0 && <Chip variant="outlined" label={`${counts.off} apagadas`} />}
                    {pending.size > 0 && <Chip variant="outlined" icon={<CircularProgress size={12} />} label="Probando contra los proveedores…" />}
                </Stack>
            )}

            {error && <Typography color="error">{error}</Typography>}

            {!overview && <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress color="warning" /></Stack>}

            {overview && groups.length === 0 && (
                <Typography color="text.secondary">{onlyProblems ? "Ninguna integración con problemas." : "No hay integraciones configuradas."}</Typography>
            )}

            {groups.map((g) => (
                <CompanyCard key={g.company.id} company={g.company} rows={g.rows} checks={checks} pending={pending}
                             onRetry={(ids) => runChecks(ids, true)} />
            ))}
        </Stack>
    );
};

export default IntegrationStatusIndex;
